import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useSnackbar } from '../../providers/SnackbarProvider';
import { useTranslation } from 'react-i18next';
import { useMutation, gql } from '@apollo/client';

// Local type until codegen is run with backend
export interface GDriveConfigEstimateType {
  sharedDriveName: string;
  directoryName: string;
  directoryId?: string | null;
  confidence: string;
  alternatives: string[];
  requiresUserConfirmation: boolean;
}

const CONFIRM_COMPANY_GDRIVE_CONFIG = gql`
  mutation confirmCompanyGdriveConfig(
    $companyId: Int!
    $sharedDriveName: String!
    $directoryName: String!
    $createIfNotExists: Boolean
  ) {
    confirmCompanyGdriveConfig(
      companyId: $companyId
      sharedDriveName: $sharedDriveName
      directoryName: $directoryName
      createIfNotExists: $createIfNotExists
    ) {
      success
      message
      directoryId
    }
  }
`;

interface GDriveConfigDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  companyId: number;
  companyName?: string;
  estimate: GDriveConfigEstimateType | null;
}

const SHARED_DRIVE_OPTIONS = [
  { value: 'SOURCING', label: 'Sourcing' },
  { value: 'EXECUTION', label: 'Execution' },
  { value: 'PORTFOLIO_FUND_1', label: 'Portfolio Fund 1' },
  { value: 'PORTFOLIO_FUND_2', label: 'Portfolio Fund 2' },
  { value: 'PORTFOLIO_FUND_3', label: 'Portfolio Fund 3' },
  { value: 'PORTFOLIO_CV_1', label: 'Portfolio CV 1' },
];

const GDriveConfigDialog: React.FC<GDriveConfigDialogProps> = ({
  open,
  onClose,
  onConfirm,
  companyId,
  companyName,
  estimate,
}) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();

  const [selectedDrive, setSelectedDrive] = useState<string>('');
  const [directoryName, setDirectoryName] = useState<string>('');
  const [createIfNotExists, setCreateIfNotExists] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const [confirmConfig] = useMutation(CONFIRM_COMPANY_GDRIVE_CONFIG);

  // Initialize from estimate when dialog opens
  useEffect(() => {
    if (estimate && open) {
      setSelectedDrive(estimate.sharedDriveName || 'SOURCING');
      setDirectoryName(estimate.directoryName || '');
      setCreateIfNotExists(!estimate.directoryId);
    }
  }, [estimate, open]);

  const handleDriveChange = (event: SelectChangeEvent<string>) => {
    setSelectedDrive(event.target.value);
  };

  const handleDirectoryNameChange = (_event: React.SyntheticEvent, value: string | null) => {
    setDirectoryName(value || '');
  };

  const handleConfirm = async () => {
    if (!selectedDrive) {
      showSnackbar('Please select a shared drive', 'warning');
      return;
    }

    if (!directoryName.trim()) {
      showSnackbar('Please enter a directory name', 'warning');
      return;
    }

    setIsConfirming(true);

    try {
      const { data } = await confirmConfig({
        variables: {
          companyId,
          sharedDriveName: selectedDrive,
          directoryName: directoryName.trim(),
          createIfNotExists,
        },
      });

      if (data?.confirmCompanyGdriveConfig?.success) {
        showSnackbar(data.confirmCompanyGdriveConfig.message || 'GDrive configuration applied successfully', 'success');
        onConfirm();
        onClose();
      } else {
        throw new Error(data?.confirmCompanyGdriveConfig?.message || 'Configuration failed');
      }
    } catch (error) {
      console.error('Error configuring GDrive:', error);
      showSnackbar(`Failed to configure GDrive: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    } finally {
      setIsConfirming(false);
    }
  };

  const handleClose = () => {
    if (!isConfirming) {
      setSelectedDrive('');
      setDirectoryName('');
      setCreateIfNotExists(false);
      onClose();
    }
  };

  const confidence = estimate?.confidence || 'low';
  const alternatives = estimate?.alternatives || [];
  const directoryOptions = [
    ...(directoryName && !alternatives.includes(directoryName) ? [directoryName] : []),
    ...alternatives,
  ];

  const getConfidenceAlert = () => {
    if (confidence === 'high') {
      return (
        <Alert severity="success" sx={{ mb: 2 }}>
          Found matching directory &quot;{estimate?.directoryName}&quot; in {estimate?.sharedDriveName}
        </Alert>
      );
    } else if (confidence === 'medium') {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          Partial match found. Please verify the configuration below.
        </Alert>
      );
    } else {
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No matching directory found. Please verify the configuration or create a new directory.
        </Alert>
      );
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableEscapeKeyDown={isConfirming}>
      <DialogTitle>Configure Google Drive Access</DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            <strong>Company:</strong> {companyName || `Company ID ${companyId}`}
          </Typography>
        </Box>

        {getConfidenceAlert()}

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="shared-drive-select-label">Shared Drive</InputLabel>
          <Select
            labelId="shared-drive-select-label"
            value={selectedDrive}
            label="Shared Drive"
            onChange={handleDriveChange}
            disabled={isConfirming}
          >
            {SHARED_DRIVE_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          freeSolo
          options={directoryOptions}
          value={directoryName}
          onInputChange={handleDirectoryNameChange}
          disabled={isConfirming}
          renderInput={params => (
            <TextField {...params} label="Directory Name" placeholder="Enter or select directory name" fullWidth />
          )}
          sx={{ mb: 2 }}
        />

        {!estimate?.directoryId && (
          <FormControlLabel
            control={
              <Checkbox
                checked={createIfNotExists}
                onChange={e => setCreateIfNotExists(e.target.checked)}
                disabled={isConfirming}
              />
            }
            label="Create directory if it doesn't exist"
          />
        )}

        {createIfNotExists && !estimate?.directoryId && (
          <Alert severity="info" sx={{ mt: 1 }}>
            A new directory will be created in {selectedDrive} with the standard folder structure.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isConfirming}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedDrive || !directoryName.trim() || isConfirming}
          startIcon={isConfirming ? <CircularProgress size={16} /> : null}
        >
          {isConfirming ? 'Configuring...' : 'Confirm & Sync'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GDriveConfigDialog;
